import { detect } from 'detect-browser';
import { type SupportedPaymentTypes, type DigitalWallet } from '@koala/sdk';
import { MOBILE } from '@/constants/styles';
import { safeHeapTrack } from '@/utils/analytics';

/**
 * Check which platform is being used.
 * userAgent ex -> Mozilla/5.0 (iPhone; CPU iPhone OS 18_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 AppId/coo-mobile Platform/ios
 */
export const parseCustomUserAgent = () => {
  if (typeof window === 'undefined') return {};

  const userAgent = window.navigator.userAgent;

  const mobileRegex = /AppId\/coo[-_]mobile/i;
  const iosRegex = /Platform\/ios/i;
  const androidRegex = /Platform\/android/i;

  return {
    mobile: mobileRegex.test(userAgent),
    mobile_ios: iosRegex.test(userAgent),
    mobile_android: androidRegex.test(userAgent),
  };
};

export type WebViewType =
  | 'gsa'
  | 'wkwebview'
  | 'mobile_safari'
  | 'desktop_safari'
  | 'chrome'
  | 'other';

/**
 * Classify the current browser/WebView from the User-Agent string.
 *
 * Used to weight Apple Pay cancellation classification: GSA WebView is
 * more aggressive about backgrounding/tearing down PaymentRequest
 * sessions, so a cancel event from GSA is more likely system-initiated
 * than diner-initiated.
 */
export const getWebViewType = (): WebViewType => {
  if (typeof window === 'undefined') return 'other';

  const ua = window.navigator.userAgent;

  if (ua.includes('GSA/')) return 'gsa';
  if (/AppId\/coo[-_]mobile/i.test(ua) && /Platform\/ios/i.test(ua)) return 'wkwebview';
  if (/CriOS\/|Chrome\//.test(ua)) return 'chrome';
  if (/iPhone|iPad|iPod/.test(ua) && ua.includes('Safari/')) return 'mobile_safari';
  if (ua.includes('Macintosh') && ua.includes('Safari/')) return 'desktop_safari';

  return 'other';
};

interface mobileStyleOptions {
  paddingLeft: string;
  paddingRight: string;
  paddingTop: string;
  paddingBottom: string;
  overflow: string;
  marginBottom: string;
  marginLeft: string;
  marginRight: string;
  backgroundColor: string;
}
export const getMobileStyles = (
  requestedOptions?: (keyof mobileStyleOptions)[],
): mobileStyleOptions => {
  const styles: mobileStyleOptions = MOBILE;

  // If no requested options are provided, return all styles
  if (!requestedOptions) {
    return styles;
  }

  // Return only the requested styles
  // @ts-expect-error {} is not mobileStyleOptions type
  return requestedOptions.reduce<mobileStyleOptions>((acc, key) => {
    if (key in styles) {
      acc[key] = styles[key];
    }
    return acc;
    // @ts-expect-error {} is not mobileStyleOptions type
  }, {});
};

export const getSupportedDigitalWallets = (
  digitalWallets: SupportedPaymentTypes['digital_wallet'],
) => {
  const userAgent = navigator.userAgent;
  const isCOOMobile_android = parseCustomUserAgent()?.mobile_android;
  const isCOOMobile_iOS = parseCustomUserAgent()?.mobile_ios;
  const isChrome =
    (userAgent.includes('Chrome') && !/Edge|Edg/.test(userAgent)) || detect()?.name === 'chrome';
  const isWebSafari = detect()?.name === 'safari';
  const isMobileBrowser_iOS = detect()?.name === 'ios';

  const supportedWallet = digitalWallets?.supported_digital_wallets?.filter(
    (wallet: DigitalWallet) => {
      // Google pay for desktop/mobile web chrome browsers
      // Disable for mobile iOS devices
      // Disable for android apps, because it is not supported for our app build
      const googlePaySupported =
        wallet.name === 'google_pay' &&
        !isCOOMobile_android &&
        !isCOOMobile_iOS &&
        !isMobileBrowser_iOS &&
        isChrome;

      // Apple pay only for iOS apps, desktop Safari and Safari wrapper browsers, mobile iOS device with none chrome browser
      const applePaySupported =
        wallet.name === 'apple_pay' &&
        !isCOOMobile_android &&
        !isChrome &&
        (isCOOMobile_iOS || isWebSafari || isMobileBrowser_iOS);

      return googlePaySupported || applePaySupported;
    },
  );

  safeHeapTrack('Payment Toggle Support Check', {
    userAgent,
    supported_wallets: supportedWallet.map((wallet) => wallet.name).join(', '),
  });

  return supportedWallet;
};
